import React, {CSSProperties, ReactNode, forwardRef, useContext, useLayoutEffect, useRef, useState} from 'react'
import ReactDOM from 'react-dom'

import {zIndex} from '../styles.js'
import ModalHeader from './ModalHeader.js'
import {ModalContext, ModalContextValue} from './ModalContext.js'
import {Overlay, closeOverlay, getModalLevel, isTopmostModal, lockBodyScroll, openOverlay} from './overlayStack.js'
import {modalStyle} from './styles.js'

export interface ModalProps {
    show: boolean
    /** Called by the close button, Escape and (closeOnBackdropClick) a click on the backdrop; the caller hides it */
    onClose?: () => void
    /** Style of the panel; a zIndex in it is put on the backdrop instead of the one from the stack */
    style?: CSSProperties
    /** Shown in a header (ModalHeader) and read out as the dialog's name */
    title?: ReactNode
    /** A close button at the right of the header */
    closeButton?: boolean
    /** Escape calls onClose (default true) */
    closeOnEscape?: boolean
    /** A click on the backdrop (pressed and released on it) calls onClose (default false) */
    closeOnBackdropClick?: boolean
    /** @deprecated use closeOnEscape={false} */
    noEsc?: boolean
    /** @deprecated use closeOnBackdropClick */
    clickClose?: boolean
    children?: ReactNode
}

const tabbableSelector = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type=hidden])', 'select:not([disabled])',
    'textarea:not([disabled])', '[tabindex]', '[contenteditable=""]', '[contenteditable=true]'
].join(', ')

// Keeps Tab and Shift+Tab inside the panel: from the last element to the first and back. Focus in a Popover panel or
// another portal opened from the modal is left alone, so its own keyboard handling keeps working.
function trapFocus(event: KeyboardEvent, panel: HTMLElement, backdrop: HTMLElement) {
    const active = document.activeElement

    if (active && active !== document.body && active !== backdrop && !panel.contains(active)) {
        return
    }

    const tabbables = Array.from(panel.querySelectorAll<HTMLElement>(tabbableSelector)).filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0)

    if (!tabbables.length) {
        event.preventDefault()
        panel.focus()
        return
    }

    const first = tabbables[0]
    const last = tabbables[tabbables.length - 1]
    const outside = !panel.contains(active)

    if (event.shiftKey && (outside || active === first || active === panel)) {
        event.preventDefault()
        last.focus()
    } else if (!event.shiftKey && (outside || active === last)) {
        event.preventDefault()
        first.focus()
    }
}

let lastTitleId = 0

/**
 * A dialog over the page: a dark backdrop with the panel top-aligned in it, 80% wide up to 800 (style changes the
 * panel). Rendered in a portal on document.body only while show, so nothing of it (no listener either) is there
 * while closed. The ref is the backdrop, which scrolls when the panel is higher than the screen.
 *
 * While open it is a modal dialog (role dialog, aria-modal, named by its title): the focus moves into it (to the panel,
 * unless something in it took the focus already), Tab stays in it, and the focus goes back to where it was on close.
 * The page behind it doesn't scroll. A Modal opened while another one is open lies above it. Escape closes only the
 * topmost overlay (see overlayStack.ts): an open Select in the modal or a Popup over it closes first.
 */
const Modal = forwardRef<HTMLDivElement, ModalProps>(function Modal(props, ref) {
    const {show, onClose, style = {}, title, closeButton, noEsc, clickClose, children} = props
    const closeOnEscape = props.closeOnEscape ?? !noEsc
    const closeOnBackdropClick = props.closeOnBackdropClick ?? !!clickClose

    const parent = useContext(ModalContext)
    const [level, setLevel] = useState(0)
    const [titles, setTitles] = useState(0)

    const titleId = useRef<string>(null)
    if (!titleId.current) {
        titleId.current = `modal-title-${++lastTitleId}`
    }

    const panelRef = useRef<HTMLDivElement>(null)
    const backdropRef = useRef<HTMLDivElement>(null)
    // Where a click on the backdrop started and ended, it only closes when both were on the backdrop itself (not a
    // text selection dragged out of the panel)
    const pressedOnBackdrop = useRef(false)
    const releasedOnBackdrop = useRef(false)

    // Read by the Escape handler, which is created once per opening
    const latest = useRef({onClose, closeOnEscape})
    latest.current = {onClose, closeOnEscape}

    const overlay = useRef<Overlay>(null)
    if (!overlay.current) {
        overlay.current = {
            kind: 'modal',
            onEscape: () => {
                const {onClose, closeOnEscape} = latest.current

                if (closeOnEscape && onClose) {
                    onClose()
                }
            }
        }
    }

    // The focus when it opens, read during the render that shows it: children with autoFocus take the focus before
    // the effect below runs
    const previousFocus = useRef<Element>(null)
    const wasShown = useRef(false)
    if (show && !wasShown.current && typeof document !== 'undefined') {
        previousFocus.current = document.activeElement
    }
    wasShown.current = show

    const contextValue = useRef<ModalContextValue>(null)
    if (!contextValue.current || contextValue.current.level !== level) {
        contextValue.current = {
            titleId: titleId.current,
            registerTitle: () => {
                setTitles((count) => count + 1)
                return () => setTitles((count) => count - 1)
            },
            level
        }
    }

    useLayoutEffect(() => {
        if (!show) {
            return
        }

        const modalOverlay = overlay.current
        const panel = panelRef.current
        const restoreFocusTo = previousFocus.current

        openOverlay(modalOverlay)
        setLevel(Math.max(getModalLevel(modalOverlay), parent ? parent.level + 1 : 0))

        const unlockScroll = lockBodyScroll()

        if (panel && !panel.contains(document.activeElement)) {
            panel.focus({preventScroll: true})
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Tab' && panelRef.current && isTopmostModal(modalOverlay)) {
                trapFocus(event, panelRef.current, backdropRef.current)
            }
        }

        document.addEventListener('keydown', onKeyDown)

        return () => {
            document.removeEventListener('keydown', onKeyDown)
            closeOverlay(modalOverlay)
            unlockScroll()

            // Back to where the focus was, unless the caller moved it out of the modal already
            const active = document.activeElement
            const focusLost = !active || active === document.body || panel?.contains(active)

            if (focusLost && restoreFocusTo instanceof HTMLElement && restoreFocusTo !== document.body && document.contains(restoreFocusTo)) {
                restoreFocusTo.focus({preventScroll: true})
            }
        }
    }, [show])

    if (!show || typeof document === 'undefined') {
        return null
    }

    const close = () => {
        onClose?.()
    }

    const {zIndex: styleZIndex, ...panelStyle} = style

    const backdropStyle: CSSProperties = {
        position: 'fixed',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        zIndex: styleZIndex ?? zIndex.modal + level,
        overflow: 'auto',
        backgroundColor: modalStyle.backdrop
    }

    const defaultPanelStyle: CSSProperties = {
        borderRadius: modalStyle.radius,
        background: modalStyle.background,
        marginTop: modalStyle.margin,
        marginBottom: modalStyle.margin,
        padding: modalStyle.padding,
        minWidth: 0,
        width: modalStyle.width,
        maxWidth: modalStyle.maxWidth,
        // The panel takes the focus on open, it has no focus ring of its own
        outline: 'none',
        ...panelStyle
    }

    const setBackdropRef = (node: HTMLDivElement) => {
        backdropRef.current = node

        if (typeof ref === 'function') {
            ref(node)
        } else if (ref) {
            ref.current = node
        }
    }

    return ReactDOM.createPortal(
        <div
            ref={setBackdropRef}
            className='veloyd_modal'
            style={backdropStyle}
            onMouseDown={(event) => {
                pressedOnBackdrop.current = event.target === event.currentTarget
            }}
            onMouseUp={(event) => {
                releasedOnBackdrop.current = event.target === event.currentTarget
            }}
            onClick={(event) => {
                if (closeOnBackdropClick && event.target === event.currentTarget && pressedOnBackdrop.current && releasedOnBackdrop.current) {
                    close()
                }

                pressedOnBackdrop.current = false
                releasedOnBackdrop.current = false
            }}
        >
            <div
                ref={panelRef}
                role='dialog'
                aria-modal='true'
                aria-labelledby={titles > 0 ? titleId.current : undefined}
                tabIndex={-1}
                style={defaultPanelStyle}
                // Clicks in the panel don't reach the backdrop or the React parents of the Modal, like before the portal
                onClick={(event) => event.stopPropagation()}
            >
                <ModalContext.Provider value={contextValue.current}>
                    {(title || closeButton) &&
                        <ModalHeader title={title} onClose={closeButton ? close : undefined} />
                    }

                    {children}
                </ModalContext.Provider>
            </div>
        </div>,
        document.body
    )
})

export default Modal
